import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { consentFormsAPI, ConsentForm } from '../services/api';
import {
  Shield,
  ArrowLeft,
  Loader,
  AlertCircle,
  Search,
  Calendar,
  User,
  FileText,
  Download,
  CheckCircle,
  XCircle,
} from 'lucide-react';

const ConsentAudit: React.FC = () => {
  const navigate = useNavigate();
  const [forms, setForms] = useState<ConsentForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [downloadingPDF, setDownloadingPDF] = useState<string | null>(null);

  useEffect(() => {
    loadAudit();
  }, []);

  const loadAudit = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await consentFormsAPI.getAuditList();
      setForms(data);
    } catch (err: any) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || err.message;
      if (status === 403) {
        setError('Acceso denegado. Solo los administradores pueden ver el historial de auditoría.');
      } else {
        setError('Error al cargar el historial: ' + (typeof detail === 'string' ? detail : JSON.stringify(detail)));
      }
      setForms([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredForms = forms.filter((form) => {
    const searchLower = searchTerm.toLowerCase();
    const patientName = form.patient_data?.nombre?.toLowerCase() || '';
    const patientDoc = form.patient_data?.documento?.toLowerCase() || '';
    const templateTitle = form.template_title?.toLowerCase() || '';
    const filledBy = (form.filled_by || '').toLowerCase();
    return (
      patientName.includes(searchLower) ||
      patientDoc.includes(searchLower) ||
      templateTitle.includes(searchLower) ||
      filledBy.includes(searchLower)
    );
  });

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-CO', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const getTipo = (form: ConsentForm) => {
    const accepts = form.consent_responses?.accepts_consent;
    if (accepts === true) return { label: 'Consentimiento', icon: CheckCircle, color: 'text-green-700 bg-green-100' };
    if (accepts === false) return { label: 'Disentimiento', icon: XCircle, color: 'text-red-700 bg-red-100' };
    return { label: 'N/A', icon: FileText, color: 'text-gray-700 bg-gray-100' };
  };

  const downloadPDF = async (formId: string, patientName: string, patientDoc: string) => {
    setDownloadingPDF(formId);
    try {
      const blob = await consentFormsAPI.downloadPDF(formId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeName = (patientName || 'documento').replace(/[^a-z0-9]/gi, '_').substring(0, 30);
      link.download = `Consentimiento_${safeName}_${patientDoc || formId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError('Error al descargar el PDF: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDownloadingPDF(null);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[45vh] flex-col items-center justify-center py-16">
        <div className="text-center">
          <Loader className="mx-auto mb-4 h-12 w-12 animate-spin text-blue-700" />
          <p className="font-medium text-slate-600">Cargando historial de auditoría…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-6 pb-4">
      <div className="max-w-7xl mx-auto">
        <div className="page-hero mb-2 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0 flex-1">
            <button type="button" onClick={() => navigate('/templates')} className="btn-secondary mb-4 py-2 text-sm">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Volver a plantillas
            </button>
            <div className="flex flex-wrap items-start gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-800 text-white shadow-md">
                <Shield className="h-6 w-6" aria-hidden />
              </div>
              <div>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-blue-700">
                  Gobierno y trazabilidad
                </p>
                <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                  Auditoría de consentimientos
                </h1>
                <p className="mt-1 max-w-2xl text-sm text-slate-600 sm:text-base">
                  Registro de quién diligenció cada consentimiento. Acceso restringido a administradores.
                </p>
              </div>
            </div>
          </div>
          <div className="flex shrink-0 items-center rounded-xl border border-slate-200/90 bg-white/80 px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm">
            Total: {forms.length} registro{forms.length !== 1 ? 's' : ''}
          </div>
        </div>

        {error && (
          <div className="card bg-red-50 border-red-200 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-red-800 font-medium">{error}</p>
              </div>
            </div>
          </div>
        )}

        {!error && (
          <>
            <div className="card mb-6">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar por paciente, documento, plantilla o usuario que llenó..."
                  className="input-field pl-12"
                />
              </div>
            </div>

            <div className="card overflow-hidden p-0">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Fecha y hora
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Usuario que llenó
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Plantilla
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Paciente
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Documento
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Tipo
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredForms.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                          {searchTerm ? 'No hay registros que coincidan con la búsqueda.' : 'No hay registros de consentimientos.'}
                        </td>
                      </tr>
                    ) : (
                      filteredForms.map((form) => {
                        const tipo = getTipo(form);
                        const TipoIcon = tipo.icon;
                        const patientName = form.patient_data?.nombre || 'N/A';
                        const patientDoc = form.patient_data?.documento || 'N/A';
                        return (
                          <tr key={form.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4 text-gray-400" />
                                {formatDate(form.filled_at)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-800 font-medium">
                              <span className="flex items-center gap-1">
                                <User className="w-4 h-4 text-gray-400" />
                                {form.filled_by || 'N/A'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate" title={form.template_title || ''}>
                              {form.template_title || 'N/A'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">{patientName}</td>
                            <td className="px-4 py-3 text-sm text-gray-600">{patientDoc}</td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${tipo.color}`}>
                                <TipoIcon className="w-3.5 h-3.5" />
                                {tipo.label}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <button
                                onClick={() => form.id && downloadPDF(form.id, patientName, patientDoc)}
                                disabled={downloadingPDF === form.id || !form.id}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 disabled:opacity-50"
                                title="Descargar PDF"
                              >
                                {downloadingPDF === form.id ? (
                                  <Loader className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Download className="w-4 h-4" />
                                )}
                                PDF
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ConsentAudit;
